import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { HttpResponse } from '@angular/common/http';
import { ClientService } from 'app/services/client-service';
import { ToastrService } from 'ngx-toastr';
import { CommonsText } from 'app/commons/CommonsText';
import { PolicyService } from 'app/services/policy-service';
import { AccountDTO } from 'app/header-footer-template/model/AccountDTO';
import { ClientDataDTO } from 'app/header-footer-template/model/ClientDataDTO';

@Component({
    selector: 'app-profile-bank-edition',
    templateUrl: './profile-bank-edition.component.html',
    styleUrls: ['./profile-bank-edition.component.scss']
})
export class ProfileBankEditionComponent implements OnInit {

    @Output() profileAction = new EventEmitter<any>();

    bankForm: FormGroup;

    messages: string[] = null;
    running: boolean = false;
    loading: boolean = false;

    accountList: any[] = [];
    policyList: any[] = [];

    showBankForm: boolean = false;
    bankDocumentName: string = null;
    bankDocumentFile: File = null;
    isDragOver: boolean = false;

    changingAccountId: any = null;

    private readonly MAX_FILE_SIZE = 10485760;

    private client: ClientDataDTO;

    constructor(
        private formBuilder: FormBuilder,
        private clientService: ClientService,
        private toastr: ToastrService,
        private policyService: PolicyService
    ) {}

    ngOnInit() {
        this.bankForm = this.formBuilder.group({
            bankIban: ['', [Validators.required, Validators.minLength(4)]],
            bankEntity: ['', [Validators.required, Validators.minLength(4)]],
            bankBranch: ['', [Validators.required, Validators.minLength(4)]],
            bankDc: ['', [Validators.required, Validators.minLength(2)]],
            bankAccount: ['', [Validators.required, Validators.minLength(10)]],
            bankHolder: ['', [Validators.required]],
            bankHolderType: ['', [Validators.required]],
            bankHolderNumber: ['', [Validators.required]]
        });

        this.bankForm.valueChanges.subscribe(() => {
            if (this.bankForm.touched || this.bankForm.dirty) {
                this.messages = this.bankForm.invalid ? [CommonsText.TODOS_CAMPOS_OBLIGATORIOS] : null;
            }
        });

        this.loadData();
    }

    private loadData() {
        this.loading = true;
        Promise.all([
            this.clientService.getClient(),
            this.clientService.getPaymentAccounts(),
            this.policyService.getCurrent()
        ]).then(([client, accountList, policyList]) => {
            this.loading = false;
            this.client = client;
            this.accountList = accountList.response;
            this.patchHolderData();
            if (policyList.response !== null && policyList.response !== undefined) {
                policyList.response.forEach(policy => {
                    if ((policy.collective === null || policy.collective === undefined) && policy.isTaker) {
                        this.policyList.push(policy);
                    } else if (policy.collective > 0 && policy.isTaker) {
                        this.policyList.push(policy);
                    }
                });
            }
        }).catch(() => {
            this.loading = false;
            this.toastr.error(CommonsText.PROBLEMA_SERVICIO_TIPOS_CUENTAS, CommonsText.PROBLEMA_DE_CONECTIVIDAD, {
                timeOut: 5000
            });
        });
    }

    private patchHolderData() {
        if (!this.client) { return; }
        let name = '';
        if (this.client.name) { name += this.client.name; }
        if (this.client.surname) { name += ' ' + this.client.surname; }
        if (this.client.lastName) { name += ' ' + this.client.lastName; }
        this.bankForm.patchValue({
            bankHolder: name.trim(),
            bankHolderType: this.client.documentType || '',
            bankHolderNumber: this.client.documentNumber || ''
        });
    }

    toggleBankForm() {
        this.showBankForm = !this.showBankForm;
    }

    onBankDocumentSelected(event: any) {
        const file = event.target.files && event.target.files[0];
        if (file) {
            this.setBankDocument(file);
        }
        event.target.value = '';
    }

    onRemoveBankDocument() {
        this.bankDocumentFile = null;
        this.bankDocumentName = null;
    }

    onDragOver(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver = true;
    }

    onDragLeave(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver = false;
    }

    onDrop(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver = false;
        const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
        if (file) {
            this.setBankDocument(file);
        }
    }

    private setBankDocument(file: File) {
        if (file.size > this.MAX_FILE_SIZE || !this.isValidFileType(file.type)) {
            this.toastr.error(CommonsText.ARCHIVO_ADJUNTO_TAMAÑO_MAXIMO_FORMATO_PERMITIDO, CommonsText.ERROR_ADJUNTANDO_ARCHIVO);
            return;
        }
        this.bankDocumentFile = file;
        this.bankDocumentName = file.name;
    }

    private isValidFileType(type: string): boolean {
        return !!type && (type.startsWith('image/') || type === CommonsText.PDF_TYPE);
    }

    captureIban(event: ClipboardEvent) {
        let pastedText = event.clipboardData.getData('text');
        if (pastedText !== undefined && pastedText !== null) {
            pastedText = pastedText.replace(/\s/g, '');
            if (pastedText.length === 24) {
                event.preventDefault();
                this.bankForm.patchValue({ bankIban: pastedText.substring(0, 4) });
                this.bankForm.patchValue({ bankEntity: pastedText.substring(4, 8) });
                this.bankForm.patchValue({ bankBranch: pastedText.substring(8, 12) });
                this.bankForm.patchValue({ bankDc: pastedText.substring(12, 14) });
                this.bankForm.patchValue({ bankAccount: pastedText.substring(14, 24) });
            }
        }
    }

    submitEnabled(): boolean {
        return !this.running && this.bankForm.valid && !!this.bankDocumentFile;
    }

    getButtonLabel(): string {
        return this.running ? CommonsText.CARGANDO : CommonsText.GUARDAR_CAMBIOS_MINUSCULA;
    }

    onSave() {
        if (this.running) { return; }

        if (this.bankForm.invalid || !this.bankDocumentFile) {
            Object.keys(this.bankForm.controls).forEach(field => {
                this.bankForm.get(field).markAsTouched({ onlySelf: true });
            });
            this.messages = [CommonsText.TODOS_CAMPOS_OBLIGATORIOS];
            return;
        }

        const account: AccountDTO = {
            iban: this.bankForm.value.bankIban,
            entity: this.bankForm.value.bankEntity,
            office: this.bankForm.value.bankBranch,
            digit: this.bankForm.value.bankDc,
            accountNumber: this.bankForm.value.bankAccount,
            titular: this.bankForm.value.bankHolder,
            documentType: [{ id: this.bankForm.value.bankHolderType, itemName: this.bankForm.value.bankHolderType }],
            documentNumber: this.bankForm.value.bankHolderNumber
        };

        const uploadFormData = new FormData();
        uploadFormData.append('file', this.bankDocumentFile);

        this.running = true;
        this.messages = null;
        this.clientService.onAddNewAccount(account, uploadFormData)
            .subscribe(
                event => {
                    if (event instanceof HttpResponse) {
                        this.running = false;
                        this.accountList = event.body.response;
                        this.showBankForm = false;
                        this.bankDocumentFile = null;
                        this.bankDocumentName = null;
                        this.bankForm.reset();
                        this.patchHolderData();
                        this.toastr.success(CommonsText.CUENTA_AYADIDA_EXITO, CommonsText.SERVICIO_DE_CUENTAS);
                        this.clientService.showModal();
                    }
                },
                error => {
                    this.running = false;
                    this.messages = (error.error && error.error.response) ? [error.error.response] : [CommonsText.PROBLEMA_DE_CONECTIVIDAD];
                    this.toastr.error(error.error.response, CommonsText.ERROR_SERVICIO_CUENTAS);
                }
            );
    }

    onCancel() {
        this.profileAction.emit({ section: 'bank' });
    }

    onChangeAccount(event: any, policy: any) {
        this.clientService.showChangeReceipts();
        const accountNumber = event.target.value;

        if (accountNumber !== '' && policy.id !== '') {
            this.changingAccountId = policy.id;
            this.clientService.onChangeAccount(accountNumber, policy.id)
                .then(() => {
                    this.changingAccountId = null;
                    policy.bankAccount = accountNumber;
                    this.toastr.success(CommonsText.CUENTA_CAMBIADA_EXITO, CommonsText.SERVICIO_DE_CUENTAS);
                }).catch(() => {
                    this.changingAccountId = null;
                    this.toastr.error(CommonsText.PROBLEMA_SERVICIO_TIPOS_CUENTAS, CommonsText.PROBLEMA_DE_CONECTIVIDAD, {
                        timeOut: 5000
                    });
                });
        }
    }

    onRemoveAccount(event: any, account: any) {
        const button = (event.target.parentNode.getElementsByTagName('button')[0]);
        const spinner = (event.target.parentNode.getElementsByTagName('i')[0]);
        button.style.display = 'none';
        spinner.style.display = 'initial';
        this.clientService.onRemoveAccount(account)
            .then(value => {
                this.accountList = value.response;
                this.toastr.success(CommonsText.CUENTA_REMOVIDA_EXITO, CommonsText.SERVICIO_DE_CUENTAS);
            }).catch(error => {
                button.style.display = 'initial';
                spinner.style.display = 'none';
                this.toastr.error(CommonsText.PROBLEMA_SERVICIO_TIPOS_CUENTAS, CommonsText.PROBLEMA_DE_CONECTIVIDAD, {
                    timeOut: 5000
                });
            });
    }
}
